import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';

export const bookmarkTabsListVariants = cva(
  'inline-flex items-center justify-center rounded-md p-1 text-[var(--chekin-color-gray-1)]',
  {
    variants: {
      variant: {
        default: [
          'relative h-auto w-full gap-1 bg-transparent p-0',
          'before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-[var(--chekin-border-default)]',
        ],
        material: [
          'relative h-auto w-full gap-1 bg-transparent p-0',
          'before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-[var(--chekin-border-default)]',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const bookmarkTabsTriggerVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap transition-all',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-brand-blue)] focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        default: [
          'overflow-hidden rounded-lg rounded-b-none border-x border-t border-[var(--chekin-border-default)]',
          'bg-[var(--chekin-color-surface-input-empty)] px-4 py-2 text-base font-semibold text-[var(--chekin-color-gray-1)]',
          'data-[state=active]:z-10 data-[state=active]:cursor-default data-[state=active]:bg-[var(--chekin-color-white)]',
          'data-[state=active]:text-[var(--chekin-color-brand-navy)] data-[state=active]:shadow-none',
          'hover:text-[var(--chekin-color-brand-navy)]',
        ],
        material: [
          'rounded-none border-0 bg-transparent px-4 py-2 text-base font-medium text-[var(--chekin-color-gray-1)]',
          'hover:text-[var(--chekin-color-brand-blue)]',
          'data-[state=active]:border-b-[3px] data-[state=active]:border-[var(--chekin-color-brand-blue)]',
          'data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-[var(--chekin-color-brand-blue)]',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BookmarkTabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof bookmarkTabsListVariants> {}

export const BookmarkTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  BookmarkTabsListProps
>(({className, variant, ...props}, ref) => (
  <TabsPrimitive.List
    ref={ref}
    data-slot="bookmark-tabs-list"
    className={cn(bookmarkTabsListVariants({variant}), className)}
    {...props}
  />
));
BookmarkTabsList.displayName = 'BookmarkTabsList';

export interface BookmarkTabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof bookmarkTabsTriggerVariants> {}

export const BookmarkTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  BookmarkTabsTriggerProps
>(({className, variant, ...props}, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    data-slot="bookmark-tabs-trigger"
    className={cn(bookmarkTabsTriggerVariants({variant}), className)}
    {...props}
  />
));
BookmarkTabsTrigger.displayName = 'BookmarkTabsTrigger';
